<script lang="ts">
	// @wc-ignore-file
	import { onMount, setContext } from 'svelte';
	import '../../app.css';
	import Footer from '$lib/Footer.svelte';
	import { createAnalyticsService } from '$lib/AnalyticsService';
	import { createPosthogService } from '$lib/PosthogService';
	import { setServiceContext } from '$lib/ServiceContext';
	import { createUserService } from '$lib/UserService.svelte';
	import { createWebpageCookieConsentService } from '$lib/CookieConsentService';
	import { createWebpageFirebaseService } from '$lib/FirebaseService';
	import { beforeNavigate } from '$app/navigation';
	import { page, updated } from '$app/state';
	import { ProgressBar } from '@prgm/sveltekit-progress-bar';
	import { createNotificationsService } from '$lib/NotificationService';
	import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
	import { createBetterAuthAuthService } from '$lib/BetterAuthAuthService.svelte';
	import { locales, type Locale } from '../../locales/data';
	import { browser } from '$app/environment';
	import _ from 'lodash';
	import { resolveLocale } from '$lib/utils.svelte';

	let { children } = $props();

	const firebaseService = createWebpageFirebaseService();
	const authService = createBetterAuthAuthService(firebaseService);
	const posthogService = createPosthogService();
	const analyticsService = createAnalyticsService(posthogService, firebaseService);
	const userService = createUserService(authService, analyticsService);
	const cookieConsentService = createWebpageCookieConsentService(
		posthogService,
		firebaseService,
		userService
	);
	const notificationService = createNotificationsService();
	setServiceContext({
		firebaseService,
		authService,
		posthogService,
		analyticsService,
		userService,
		cookieConsentService,
		notificationService
	});

	onMount(() => {
		cookieConsentService.initCookieConsent();

		const analyticsId = page.url.searchParams.get('log-analytics-id');
		if (analyticsId) {
			cookieConsentService.giveConsent();
			posthogService.init();
			analyticsService.setPersonProperties({ 'log-analytics-id': analyticsId });
		}
	});

	const initalLoad = $state({ value: true });
	setContext('initialLoad', initalLoad);

	beforeNavigate(() => {
		initalLoad.value = false;
	});

	beforeNavigate(({ willUnload, to }) => {
		if (updated.current && !willUnload && to?.url) {
			location.href = to.url.href;
		}
	});

	const resolveCurrentPage = (locale: Locale) => {
		if (browser) {
			return resolveLocale(page.route.id, locale);
		}
		const relativePath = resolveLocale(page.route.id, locale);
		return _.trimStart(relativePath, '.');
	};

	const makeCanonicalUrl = (locale: Locale) => {
		return `${PUBLIC_VOICE_ALIGN_WEBSITE}${resolveCurrentPage(locale)}`;
	};

	const canonical = $derived(makeCanonicalUrl(page.data.locale));
	const languageAlternates = $derived.by(() => {
		if (!page.data.locale) {
			return undefined;
		}
		const alternatives = locales.map<{ hreflang: Locale | 'x-default'; href: string }>(
			(locale) => ({
				hreflang: locale,
				href: makeCanonicalUrl(locale)
			})
		);
		alternatives.push({
			hreflang: 'x-default',
			href: makeCanonicalUrl('en')
		});
		return alternatives;
	});
</script>

<svelte:head>
	<script
		defer
		fetchpriority="high"
		src="https://cdnjs.cloudflare.com/polyfill/v3/polyfill.min.js?version=4.8.0&features=default%2Ces7%2Ces6%2Ces5"
	></script>
	<link rel="canonical" href={canonical} />
	{#each languageAlternates as alternative}
		<link rel="alternate" href={alternative.href} hreflang={alternative.hreflang} />
	{/each}
</svelte:head>

{#await import('$lib/shadcn/components/ui/sonner/sonner.svelte') then { default: Toaster }}
	<Toaster theme="dark" />
{/await}
{#await posthogService
	.init()
	.then(async () => import('$lib/components/LazyInit.svelte')) then { default: LazyInit }}
	<LazyInit callback={posthogService.initNavigation} />
{/await}

<!-- Disable while using GCP -->
<!--<ServiceUnavailableOverlay />-->
<ProgressBar class="text-primary" zIndex={1000} />
<div class="grid min-h-screen grid-cols-1 grid-rows-[minmax(0,1fr)_auto]">
	<div class="col-start-1 row-start-1 min-h-screen">
		{@render children()}
	</div>
	<div class="col-start-1 row-start-2 min-h-0">
		<Footer />
	</div>
</div>
